import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE, errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Mic, Video, Images, Award, Newspaper, FileDown,
    Upload, Trash2, Play, Pause, StopCircle, Lock, CheckCircle2, ExternalLink,
} from "lucide-react";

/** Shared helper: multipart POST with auth header + friendly error. */
async function postMultipart(path, formData) {
    const token = localStorage.getItem("ddi_token");
    const resp = await fetch(`${API_BASE}${path}`, {
        method: "POST", body: formData,
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        const detail = j?.detail;
        if (typeof detail === "object" && detail?.code === "kind_locked") {
            throw new Error(`${detail.message || "Feature locked for your package"} (${detail.feature_code})`);
        }
        throw new Error((typeof detail === "string" ? detail : null) || `Request failed (${resp.status})`);
    }
    return resp.json();
}

function LockedState({ feature_code, children }) {
    return (
        <div className="rounded-2xl border border-dashed border-amber-300 bg-amber-50 p-8 text-center" data-testid="b3-locked-state">
            <Lock size={28} className="mx-auto text-amber-700 mb-2" />
            <div className="font-semibold text-amber-900">Not included in your current package</div>
            <div className="text-sm text-amber-800 mt-1">Upgrade to unlock this feature. Feature code: <strong>{feature_code}</strong></div>
            <Button size="sm" asChild className="mt-4" data-testid="b3-locked-upgrade">
                <a href="/doctor/packages">View upgrade options</a>
            </Button>
            {children}
        </div>
    );
}

// ───────────────── 1. VOICE INTRO ─────────────────
function VoiceIntroTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [transcript, setTranscript] = useState("");
    const [uploading, setUploading] = useState(false);
    const [recording, setRecording] = useState(false);
    const [recordedBlob, setRecordedBlob] = useState(null);
    const mediaRecRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const [elapsed, setElapsed] = useState(0);

    async function load() {
        setLoading(true);
        try {
            const r = await api.get("/me/b3/voice-intro");
            setData(r.data);
            setTranscript(r.data?.voice_intro?.transcript || "");
        } catch (e) {
            toast.error(errMsg(e, "Failed to load voice intro"));
        } finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function uploadFile(ev) {
        const file = ev.target.files?.[0]; if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("transcript", transcript || "");
            fd.append("is_visible", "true");
            await postMultipart("/me/b3/voice-intro", fd);
            toast.success("Voice intro uploaded");
            ev.target.value = "";
            await load();
        } catch (e) { toast.error(e.message); }
        finally { setUploading(false); }
    }

    async function uploadRecording() {
        if (!recordedBlob) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", recordedBlob, "voice_intro.webm");
            fd.append("transcript", transcript || "");
            fd.append("is_visible", "true");
            await postMultipart("/me/b3/voice-intro", fd);
            toast.success("Voice intro saved");
            setRecordedBlob(null);
            await load();
        } catch (e) { toast.error(e.message); }
        finally { setUploading(false); }
    }

    async function startRecording() {
        if (!navigator.mediaDevices?.getUserMedia) {
            toast.error("Browser recording not available — use the upload option below.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
            mr.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                setRecordedBlob(blob);
                stream.getTracks().forEach((t) => t.stop());
                clearInterval(timerRef.current);
            };
            mediaRecRef.current = mr; mr.start();
            setRecording(true); setElapsed(0);
            timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
            // Auto-stop at 60s to respect spec.
            setTimeout(() => { if (mr.state === "recording") mr.stop(); setRecording(false); }, 60000);
        } catch (e) {
            toast.error("Microphone permission denied — use the upload option below.");
        }
    }

    function stopRecording() {
        if (mediaRecRef.current?.state === "recording") mediaRecRef.current.stop();
        setRecording(false);
    }

    async function toggleVisible() {
        try {
            await api.put("/me/b3/voice-intro", { is_visible: !data.voice_intro.is_visible });
            await load();
        } catch (e) { toast.error("Toggle failed"); }
    }
    async function saveTranscript() {
        try {
            await api.put("/me/b3/voice-intro", { transcript });
            toast.success("Transcript saved");
            await load();
        } catch (e) { toast.error("Save failed"); }
    }
    async function remove() {
        if (!confirm("Delete your voice intro?")) return;
        try {
            await api.delete("/me/b3/voice-intro");
            toast.success("Removed");
            setRecordedBlob(null);
            await load();
        } catch (e) { toast.error("Delete failed"); }
    }

    if (loading) return <div className="text-sm text-slate-500" data-testid="voice-loading">Loading…</div>;
    if (!data?.unlocked) return <LockedState feature_code={data?.feature_code || "I05"} />;

    const vi = data.voice_intro;
    return (
        <div className="space-y-5" data-testid="voice-intro-tab">
            <p className="text-sm text-[#64748B]">
                Record or upload a short (30–60 sec) audio introduction that plays on your public profile. Keep it warm, honest and professional.
            </p>
            {vi?.audio_url && (
                <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 flex items-center gap-5" data-testid="voice-current">
                    <div className="w-14 h-14 rounded-full bg-[#123B5D] text-white flex items-center justify-center">
                        <Mic size={22} />
                    </div>
                    <div className="flex-1">
                        <div className="font-semibold text-[#123B5D]">Current voice intro</div>
                        <audio src={vi.audio_url} controls className="mt-2 w-full max-w-md" data-testid="voice-player" />
                    </div>
                    <div className="flex items-center gap-2">
                        <Switch checked={!!vi.is_visible} onCheckedChange={toggleVisible} data-testid="voice-visible-toggle" />
                        <Button variant="ghost" size="icon" onClick={remove} data-testid="voice-delete">
                            <Trash2 size={16} className="text-rose-600" />
                        </Button>
                    </div>
                </div>
            )}

            <div className="rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-6">
                <div className="font-semibold text-[#123B5D] mb-2">Record from browser</div>
                <div className="flex items-center gap-3">
                    {!recording ? (
                        <Button onClick={startRecording} data-testid="voice-record-start" className="bg-[#DC2626] hover:bg-[#b91c1c]">
                            <Mic size={16} /> Start recording
                        </Button>
                    ) : (
                        <Button onClick={stopRecording} data-testid="voice-record-stop" variant="outline">
                            <StopCircle size={16} /> Stop ({elapsed}s)
                        </Button>
                    )}
                    {recordedBlob && (
                        <>
                            <audio src={URL.createObjectURL(recordedBlob)} controls data-testid="voice-record-preview" />
                            <Button onClick={uploadRecording} disabled={uploading} data-testid="voice-record-save">
                                <Upload size={16} /> {uploading ? "Saving…" : "Save as voice intro"}
                            </Button>
                        </>
                    )}
                </div>
                <div className="mt-5">
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Or upload an audio file</label>
                    <input type="file" accept="audio/*" onChange={uploadFile} disabled={uploading}
                        className="block mt-2 text-sm" data-testid="voice-upload-input" />
                    <div className="text-[11px] text-[#64748B] mt-1">MP3, WAV, M4A, OGG or WebM. Max 15 MB.</div>
                </div>
            </div>

            <div>
                <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Transcript (optional, improves SEO + accessibility)</label>
                <Textarea rows={3} value={transcript} onChange={(e) => setTranscript(e.target.value)}
                    maxLength={4000} className="mt-2" data-testid="voice-transcript-input"
                    placeholder="A short text version of what you say in the intro…" />
                {vi?.audio_url && (
                    <Button variant="outline" onClick={saveTranscript} className="mt-2" data-testid="voice-transcript-save">
                        Save transcript
                    </Button>
                )}
            </div>
        </div>
    );
}

// ───────────────── 2. VIDEO TESTIMONIALS ─────────────────
function VideoTestimonialsTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ youtube_url: "", video_url: "", patient_initials: "", condition: "", caption: "" });
    async function load() {
        try {
            const r = await api.get("/me/b3/video-testimonials");
            setData(r.data);
        } catch (e) { toast.error("Failed to load"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function add() {
        if (!form.youtube_url && !form.video_url) { toast.error("Provide a YouTube URL or MP4 URL"); return; }
        if (!form.patient_initials) { toast.error("Patient initials required"); return; }
        try {
            await api.post("/me/b3/video-testimonials", form);
            setForm({ youtube_url: "", video_url: "", patient_initials: "", condition: "", caption: "" });
            toast.success("Testimonial added");
            await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : (d?.message || "Add failed"));
        }
    }
    async function remove(id) {
        if (!confirm("Remove this testimonial?")) return;
        await api.delete(`/me/b3/video-testimonials/${id}`);
        toast.success("Removed");
        await load();
    }
    async function toggle(it) {
        await api.patch(`/me/b3/video-testimonials/${it.id}`, { is_visible: !it.is_visible });
        await load();
    }

    if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
    if (!data?.unlocked) return <LockedState feature_code={data?.feature_code || "G02"} />;

    return (
        <div className="space-y-5" data-testid="video-testimonials-tab">
            <p className="text-sm text-[#64748B]">
                Add short patient video testimonials. YouTube is preferred (private videos won't play). Patients should use initials — never full names.
            </p>
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input placeholder="YouTube URL (youtube.com/watch?v=…)" value={form.youtube_url}
                    onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                    data-testid="vtest-youtube-url" />
                <Input placeholder="…or direct MP4 URL" value={form.video_url}
                    onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                    data-testid="vtest-video-url" />
                <Input placeholder="Patient initials (e.g. A.M.)" value={form.patient_initials}
                    onChange={(e) => setForm({ ...form, patient_initials: e.target.value })}
                    maxLength={10} data-testid="vtest-initials" />
                <Input placeholder="Condition (optional)" value={form.condition}
                    onChange={(e) => setForm({ ...form, condition: e.target.value })}
                    maxLength={120} data-testid="vtest-condition" />
                <Textarea placeholder="Caption (optional) — 1–2 lines" value={form.caption}
                    onChange={(e) => setForm({ ...form, caption: e.target.value })}
                    maxLength={400} className="md:col-span-2" data-testid="vtest-caption" />
                <div className="md:col-span-2">
                    <Button onClick={add} data-testid="vtest-add">
                        <Upload size={16} /> Add testimonial
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(data.items || []).map((it) => (
                    <div key={it.id} className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden"
                        data-testid={`vtest-item-${it.id}`}>
                        {it.youtube_id ? (
                            <iframe src={`https://www.youtube-nocookie.com/embed/${it.youtube_id}`}
                                className="w-full aspect-video" allowFullScreen title={it.caption || "testimonial"} />
                        ) : (
                            <video src={it.video_url} controls className="w-full aspect-video bg-black" />
                        )}
                        <div className="p-3 flex items-center justify-between gap-3">
                            <div>
                                <div className="text-sm font-semibold text-[#123B5D]">{it.patient_initials}
                                    {it.condition && <span className="text-xs text-slate-500 ml-2">· {it.condition}</span>}
                                </div>
                                {it.caption && <div className="text-xs text-slate-600 mt-1">{it.caption}</div>}
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch checked={!!it.is_visible} onCheckedChange={() => toggle(it)}
                                    data-testid={`vtest-toggle-${it.id}`} />
                                <Button variant="ghost" size="icon" onClick={() => remove(it.id)}
                                    data-testid={`vtest-delete-${it.id}`}>
                                    <Trash2 size={15} className="text-rose-600" />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
                {!(data.items || []).length && (
                    <div className="col-span-full text-sm text-[#64748B] rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-6 text-center">
                        No testimonials yet. Add the first above.
                    </div>
                )}
            </div>
        </div>
    );
}

// ───────────────── 3. CASE GALLERY ─────────────────
function CaseGalleryTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({
        title: "", description: "", before_label: "Before", after_label: "After",
        patient_initials: "", consent_attestation: false,
    });
    const beforeRef = useRef(null);
    const afterRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    async function load() {
        try {
            const r = await api.get("/me/b3/case-gallery");
            setData(r.data);
        } catch (e) { toast.error("Failed to load"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function create() {
        if (!form.consent_attestation) return toast.error("Please attest to the consent checkbox");
        if (!form.patient_initials) return toast.error("Patient initials required");
        const b = beforeRef.current?.files?.[0];
        const a = afterRef.current?.files?.[0];
        if (!b || !a) return toast.error("Both before AND after images are required");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("before_image", b);
            fd.append("after_image", a);
            fd.append("patient_initials", form.patient_initials);
            fd.append("consent_attestation", "true");
            fd.append("title", form.title);
            fd.append("description", form.description);
            fd.append("before_label", form.before_label);
            fd.append("after_label", form.after_label);
            await postMultipart("/me/b3/case-gallery", fd);
            toast.success("Case added");
            setForm({ title: "", description: "", before_label: "Before", after_label: "After",
                      patient_initials: "", consent_attestation: false });
            if (beforeRef.current) beforeRef.current.value = "";
            if (afterRef.current)  afterRef.current.value  = "";
            await load();
        } catch (e) { toast.error(e.message); }
        finally { setUploading(false); }
    }
    async function remove(id) {
        if (!confirm("Delete this case?")) return;
        await api.delete(`/me/b3/case-gallery/${id}`); await load();
    }
    async function toggle(it) {
        await api.patch(`/me/b3/case-gallery/${it.id}`, { is_visible: !it.is_visible });
        await load();
    }
    async function uploadConsentDoc(id, file) {
        if (!file) return;
        const fd = new FormData(); fd.append("file", file);
        try {
            await postMultipart(`/me/b3/case-gallery/${id}/consent-doc`, fd);
            toast.success("Consent document uploaded (kept private)");
            await load();
        } catch (e) { toast.error(e.message); }
    }

    if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
    if (!data?.unlocked) return <LockedState feature_code={data?.feature_code || "I06"} />;

    return (
        <div className="space-y-5" data-testid="case-gallery-tab">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-[13px] text-amber-900">
                <strong>Before uploading:</strong> you have signed written consent from the patient, images are
                watermark-free, and faces/identifiable features are either not visible or anonymised.
                Your attestation is logged with timestamp. {data.disclaimer}
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">Before image</label>
                    <input ref={beforeRef} type="file" accept="image/jpeg,image/png,image/webp"
                        className="block w-full mt-2 text-sm" data-testid="case-before-file" />
                </div>
                <div>
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">After image</label>
                    <input ref={afterRef} type="file" accept="image/jpeg,image/png,image/webp"
                        className="block w-full mt-2 text-sm" data-testid="case-after-file" />
                </div>
                <Input placeholder="Case title (e.g. Acne scar revision)" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    maxLength={200} data-testid="case-title" />
                <Input placeholder="Patient initials" value={form.patient_initials}
                    onChange={(e) => setForm({ ...form, patient_initials: e.target.value })}
                    maxLength={10} data-testid="case-initials" />
                <Input placeholder="Before label" value={form.before_label}
                    onChange={(e) => setForm({ ...form, before_label: e.target.value })} maxLength={60} />
                <Input placeholder="After label" value={form.after_label}
                    onChange={(e) => setForm({ ...form, after_label: e.target.value })} maxLength={60} />
                <Textarea placeholder="Short clinical description (optional)" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    maxLength={2000} className="md:col-span-2" data-testid="case-description" />
                <label className="md:col-span-2 flex items-start gap-3 text-sm text-[#334155] cursor-pointer">
                    <input type="checkbox" checked={form.consent_attestation}
                        onChange={(e) => setForm({ ...form, consent_attestation: e.target.checked })}
                        className="mt-1" data-testid="case-consent-attest" />
                    <span>
                        I confirm I have obtained <strong>written consent</strong> from the patient
                        ({form.patient_initials || "initials"}) to publish these images for professional
                        education and marketing on this profile, and that the images are watermark-free
                        and anonymised where required. This attestation is logged with timestamp.
                    </span>
                </label>
                <div className="md:col-span-2">
                    <Button onClick={create} disabled={uploading} data-testid="case-add">
                        <Upload size={16} /> {uploading ? "Uploading…" : "Add case"}
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(data.items || []).map((it) => (
                    <div key={it.id} className="rounded-2xl border border-[#E2E8F0] bg-white overflow-hidden"
                        data-testid={`case-item-${it.id}`}>
                        <div className="grid grid-cols-2">
                            <div className="relative aspect-[4/3]">
                                <img src={it.before_image_url} alt="before" className="w-full h-full object-cover" />
                                <span className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full">{it.before_label}</span>
                            </div>
                            <div className="relative aspect-[4/3]">
                                <img src={it.after_image_url} alt="after" className="w-full h-full object-cover" />
                                <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full">{it.after_label}</span>
                            </div>
                        </div>
                        <div className="p-3">
                            <div className="flex items-center justify-between gap-2">
                                <div>
                                    <div className="text-sm font-semibold text-[#123B5D]">{it.title || "Untitled case"}</div>
                                    <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-2 flex-wrap">
                                        <Badge variant="secondary">Patient: {it.patient_initials}</Badge>
                                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                                            <CheckCircle2 size={12} /> Consent on file
                                        </span>
                                        {it.consent_doc && (
                                            <span className="inline-flex items-center gap-1 text-slate-700">
                                                📎 {it.consent_doc.filename}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch checked={!!it.is_visible} onCheckedChange={() => toggle(it)}
                                        data-testid={`case-toggle-${it.id}`} />
                                    <Button variant="ghost" size="icon" onClick={() => remove(it.id)}
                                        data-testid={`case-delete-${it.id}`}>
                                        <Trash2 size={15} className="text-rose-600" />
                                    </Button>
                                </div>
                            </div>
                            {!it.consent_doc && (
                                <label className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#123B5D] cursor-pointer underline">
                                    Attach signed consent document (optional, stored privately)
                                    <input type="file" accept="application/pdf,image/*" className="hidden"
                                        onChange={(e) => uploadConsentDoc(it.id, e.target.files?.[0])}
                                        data-testid={`case-consent-doc-${it.id}`} />
                                </label>
                            )}
                            {it.description && <p className="text-xs text-slate-600 mt-2 leading-relaxed">{it.description}</p>}
                        </div>
                    </div>
                ))}
                {!(data.items || []).length && (
                    <div className="col-span-full text-sm text-[#64748B] rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-6 text-center">
                        No case studies yet.
                    </div>
                )}
            </div>
        </div>
    );
}

// ───────────────── 4. AWARDS WITH CERTIFICATES ─────────────────
function AwardsTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ title: "", issuer: "", year: "", description: "", external_link: "" });
    async function load() {
        try { const r = await api.get("/me/b3/awards"); setData(r.data); }
        catch (e) { toast.error("Failed to load"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function add() {
        if (!form.title) return toast.error("Title required");
        try {
            await api.post("/me/b3/awards", form);
            toast.success("Award added");
            setForm({ title: "", issuer: "", year: "", description: "", external_link: "" });
            await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Add failed");
        }
    }
    async function uploadCert(aid, file) {
        if (!file) return;
        const fd = new FormData(); fd.append("file", file);
        try {
            await postMultipart(`/me/b3/awards/${aid}/certificate`, fd);
            toast.success("Certificate uploaded");
            await load();
        } catch (e) { toast.error(e.message); }
    }
    async function toggle(it) { await api.patch(`/me/b3/awards/${it.id}`, { is_visible: !it.is_visible }); await load(); }
    async function remove(id) { if (!confirm("Delete this award?")) return; await api.delete(`/me/b3/awards/${id}`); await load(); }

    if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
    if (!data?.unlocked) return <LockedState feature_code={data?.feature_code || "F04"} />;

    return (
        <div className="space-y-5" data-testid="awards-tab">
            <p className="text-sm text-[#64748B]">
                Add awards &amp; recognitions. Attach a certificate PDF or image so visitors can verify.
            </p>
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input placeholder="Award title" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    maxLength={200} data-testid="award-title" />
                <Input placeholder="Issuing body" value={form.issuer}
                    onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                    maxLength={200} data-testid="award-issuer" />
                <Input placeholder="Year (e.g. 2024)" value={form.year}
                    onChange={(e) => setForm({ ...form, year: e.target.value })}
                    maxLength={10} data-testid="award-year" />
                <Input placeholder="External link (optional)" value={form.external_link}
                    onChange={(e) => setForm({ ...form, external_link: e.target.value })}
                    maxLength={500} data-testid="award-link" />
                <Textarea placeholder="Short description (optional)" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    maxLength={1000} className="md:col-span-2" data-testid="award-desc" />
                <div className="md:col-span-2">
                    <Button onClick={add} data-testid="award-add"><Upload size={16} /> Add award</Button>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(data.items || []).map((it) => (
                    <div key={it.id} className="rounded-2xl border border-[#E2E8F0] bg-white p-4"
                        data-testid={`award-item-${it.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                                <div className="font-bold text-[#123B5D]">{it.title}</div>
                                <div className="text-xs text-slate-500 mt-0.5">{[it.issuer, it.year].filter(Boolean).join(" · ")}</div>
                                {it.description && <p className="text-sm text-slate-600 mt-2 leading-relaxed">{it.description}</p>}
                                <div className="mt-3 flex items-center gap-2 flex-wrap">
                                    {it.certificate_url ? (
                                        <a href={it.certificate_url} target="_blank" rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#123B5D] underline"
                                            data-testid={`award-cert-link-${it.id}`}>
                                            <ExternalLink size={13} /> {it.certificate_filename || "View certificate"}
                                        </a>
                                    ) : (
                                        <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#123B5D] cursor-pointer underline">
                                            Upload certificate (PDF/image)
                                            <input type="file" accept="application/pdf,image/*" className="hidden"
                                                onChange={(e) => uploadCert(it.id, e.target.files?.[0])}
                                                data-testid={`award-cert-upload-${it.id}`} />
                                        </label>
                                    )}
                                    {it.external_link && (
                                        <a href={it.external_link} target="_blank" rel="noopener noreferrer"
                                            className="text-xs text-slate-600 underline">External link ↗</a>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch checked={!!it.is_visible} onCheckedChange={() => toggle(it)}
                                    data-testid={`award-toggle-${it.id}`} />
                                <Button variant="ghost" size="icon" onClick={() => remove(it.id)}
                                    data-testid={`award-delete-${it.id}`}>
                                    <Trash2 size={15} className="text-rose-600" />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
                {!(data.items || []).length && (
                    <div className="col-span-full text-sm text-[#64748B] rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-6 text-center">
                        No awards yet.
                    </div>
                )}
            </div>
        </div>
    );
}

// ───────────────── 5. MEDIA MENTIONS ─────────────────
function MediaMentionsTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ outlet_name: "", article_title: "", url: "", published_on: "" });
    async function load() {
        try { const r = await api.get("/me/b3/media-mentions"); setData(r.data); }
        catch (e) { toast.error("Failed to load"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);
    async function add() {
        if (!form.outlet_name) return toast.error("Outlet name required");
        try {
            await api.post("/me/b3/media-mentions", form);
            setForm({ outlet_name: "", article_title: "", url: "", published_on: "" });
            toast.success("Media mention added");
            await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Add failed");
        }
    }
    async function uploadLogo(mid, file) {
        if (!file) return;
        const fd = new FormData(); fd.append("file", file);
        try {
            await postMultipart(`/me/b3/media-mentions/${mid}/logo`, fd);
            toast.success("Logo uploaded"); await load();
        } catch (e) { toast.error(e.message); }
    }
    async function toggle(it) { await api.patch(`/me/b3/media-mentions/${it.id}`, { is_visible: !it.is_visible }); await load(); }
    async function remove(id) { if (!confirm("Delete this mention?")) return; await api.delete(`/me/b3/media-mentions/${id}`); await load(); }

    if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
    if (!data?.unlocked) return <LockedState feature_code={data?.feature_code || "F03"} />;

    return (
        <div className="space-y-5" data-testid="media-mentions-tab">
            <p className="text-sm text-[#64748B]">
                Add publications you've been featured in. These appear as an "As seen in" marquee strip on your public profile.
            </p>
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input placeholder="Outlet (e.g. Times of India)" value={form.outlet_name}
                    onChange={(e) => setForm({ ...form, outlet_name: e.target.value })}
                    maxLength={120} data-testid="mm-outlet" />
                <Input placeholder="Published date (optional)" value={form.published_on}
                    onChange={(e) => setForm({ ...form, published_on: e.target.value })}
                    maxLength={20} data-testid="mm-date" />
                <Input placeholder="Article title (optional)" value={form.article_title}
                    onChange={(e) => setForm({ ...form, article_title: e.target.value })}
                    maxLength={240} data-testid="mm-title" />
                <Input placeholder="Article URL (optional)" value={form.url}
                    onChange={(e) => setForm({ ...form, url: e.target.value })}
                    maxLength={500} data-testid="mm-url" />
                <div className="md:col-span-2">
                    <Button onClick={add} data-testid="mm-add"><Upload size={16} /> Add mention</Button>
                </div>
            </div>
            <div className="space-y-2">
                {(data.items || []).map((it) => (
                    <div key={it.id} className="rounded-xl border border-[#E2E8F0] bg-white p-3 flex items-center gap-3"
                        data-testid={`mm-item-${it.id}`}>
                        {it.logo_url ? (
                            <img src={it.logo_url} alt={it.outlet_name} className="h-8 w-20 object-contain" />
                        ) : (
                            <label className="inline-flex items-center justify-center h-8 w-20 border border-dashed border-slate-300 rounded text-[10px] text-slate-500 cursor-pointer">
                                + Logo
                                <input type="file" accept="image/*" className="hidden"
                                    onChange={(e) => uploadLogo(it.id, e.target.files?.[0])}
                                    data-testid={`mm-logo-${it.id}`} />
                            </label>
                        )}
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-[#123B5D] truncate">{it.outlet_name}</div>
                            <div className="text-xs text-slate-500 truncate">
                                {[it.article_title, it.published_on].filter(Boolean).join(" · ")}
                            </div>
                        </div>
                        {it.url && (
                            <a href={it.url} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-slate-600 underline">Open ↗</a>
                        )}
                        <Switch checked={!!it.is_visible} onCheckedChange={() => toggle(it)}
                            data-testid={`mm-toggle-${it.id}`} />
                        <Button variant="ghost" size="icon" onClick={() => remove(it.id)}
                            data-testid={`mm-delete-${it.id}`}>
                            <Trash2 size={15} className="text-rose-600" />
                        </Button>
                    </div>
                ))}
                {!(data.items || []).length && (
                    <div className="text-sm text-[#64748B] rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-6 text-center">
                        No media mentions yet.
                    </div>
                )}
            </div>
        </div>
    );
}

// ───────────────── 6. HEALTH GUIDES ─────────────────
function HealthGuidesTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ title: "", summary: "", category: "" });
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiTitle, setAiTitle] = useState("");
    const [aiAudience, setAiAudience] = useState("");
    const [aiBusy, setAiBusy] = useState(false);
    const [aiDraft, setAiDraft] = useState(null);
    const [publishBusy, setPublishBusy] = useState(false);

    async function load() {
        try { const r = await api.get("/me/b3/health-guides"); setData(r.data); }
        catch (e) { toast.error("Failed to load"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function add() {
        if (!form.title) return toast.error("Title required");
        const f = fileRef.current?.files?.[0]; if (!f) return toast.error("Select a PDF");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("title", form.title);
            fd.append("summary", form.summary);
            fd.append("category", form.category);
            await postMultipart("/me/b3/health-guides", fd);
            toast.success("Guide uploaded");
            setForm({ title: "", summary: "", category: "" });
            if (fileRef.current) fileRef.current.value = "";
            await load();
        } catch (e) { toast.error(e.message); }
        finally { setUploading(false); }
    }
    async function toggle(it) { await api.patch(`/me/b3/health-guides/${it.id}`, { is_visible: !it.is_visible }); await load(); }
    async function remove(id) { if (!confirm("Delete this guide?")) return; await api.delete(`/me/b3/health-guides/${id}`); await load(); }

    async function aiDraftRun() {
        if (!aiTitle.trim()) return toast.error("Give the guide a title first");
        setAiBusy(true);
        try {
            const r = await api.post("/me/b4/health-guides/draft", { title: aiTitle, audience: aiAudience });
            setAiDraft(r.data);
            toast.success("Draft ready — review before publishing");
        } catch (e) {
            toast.error(errMsg(e, "AI drafting failed"));
        } finally { setAiBusy(false); }
    }
    async function aiPublish() {
        if (!aiDraft?.id) return;
        setPublishBusy(true);
        try {
            await api.post(`/me/b4/health-guides/drafts/${aiDraft.id}/publish`);
            toast.success("AI-drafted guide published");
            setAiOpen(false);
            setAiTitle(""); setAiAudience(""); setAiDraft(null);
            await load();
        } catch (e) {
            toast.error("Publish failed");
        } finally { setPublishBusy(false); }
    }
    function updateDraftSection(idx, body) {
        setAiDraft({
            ...aiDraft,
            sections: aiDraft.sections.map((s, i) => i === idx ? { ...s, body } : s),
        });
    }
    async function saveDraftEdits() {
        if (!aiDraft?.id) return;
        try {
            await api.patch(`/me/b4/health-guides/drafts/${aiDraft.id}`, {
                title: aiDraft.title, sections: aiDraft.sections, disclaimer: aiDraft.disclaimer,
            });
            toast.success("Edits saved");
        } catch (e) { toast.error("Save failed"); }
    }

    if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
    if (!data?.unlocked) return <LockedState feature_code={data?.feature_code || "D05"} />;

    return (
        <div className="space-y-5" data-testid="health-guides-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm text-[#64748B] max-w-2xl">
                    Upload branded PDF health guides your patients can download for free. Keep each guide under 10 MB.
                    Or let Claude draft one for you — you review and edit before it publishes.
                </p>
                <Button variant="outline" onClick={() => setAiOpen(true)} data-testid="guide-ai-draft-open"
                    className="border-[#0F766E]/40 text-[#0F766E] hover:bg-[#0F766E]/10">
                    ✨ Draft with AI
                </Button>
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input placeholder="Guide title" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    maxLength={200} data-testid="guide-title" />
                <Input placeholder="Category (e.g. Pregnancy, Diabetes)" value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    maxLength={80} data-testid="guide-category" />
                <Textarea placeholder="Short summary (optional, 1–2 lines)" value={form.summary}
                    onChange={(e) => setForm({ ...form, summary: e.target.value })}
                    maxLength={600} className="md:col-span-2" data-testid="guide-summary" />
                <div className="md:col-span-2">
                    <label className="text-xs font-semibold text-[#64748B] uppercase tracking-wider">PDF file</label>
                    <input ref={fileRef} type="file" accept="application/pdf"
                        className="block w-full mt-2 text-sm" data-testid="guide-file" />
                </div>
                <div className="md:col-span-2">
                    <Button onClick={add} disabled={uploading} data-testid="guide-add">
                        <Upload size={16} /> {uploading ? "Uploading…" : "Upload guide"}
                    </Button>
                </div>
            </div>

            {aiOpen && (
                <div className="rounded-2xl border-2 border-[#0F766E]/30 bg-gradient-to-br from-[#0F766E]/5 to-white p-5 space-y-4"
                    data-testid="guide-ai-modal">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="font-bold text-[#123B5D] text-base flex items-center gap-2">
                                ✨ AI-drafted health guide
                            </div>
                            <p className="text-xs text-[#64748B] mt-1">
                                Powered by Claude. The draft is a starting point — <strong>always review and edit</strong> before publishing.
                            </p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setAiOpen(false); setAiDraft(null); }}
                            data-testid="guide-ai-close">Close</Button>
                    </div>

                    {!aiDraft ? (
                        <div className="space-y-3">
                            <Input placeholder="Guide title (e.g. 'A parent's guide to toddler fevers')"
                                value={aiTitle} onChange={(e) => setAiTitle(e.target.value)}
                                maxLength={200} data-testid="guide-ai-title" />
                            <Input placeholder="Primary audience (optional, e.g. 'parents of under-5s')"
                                value={aiAudience} onChange={(e) => setAiAudience(e.target.value)}
                                maxLength={120} data-testid="guide-ai-audience" />
                            <Button onClick={aiDraftRun} disabled={aiBusy} data-testid="guide-ai-generate"
                                className="bg-[#0F766E] hover:bg-[#115e59]">
                                {aiBusy ? "Drafting (10–20 sec)…" : "Generate draft"}
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="guide-ai-editor">
                            <Input value={aiDraft.title}
                                onChange={(e) => setAiDraft({ ...aiDraft, title: e.target.value })}
                                className="font-semibold" data-testid="guide-ai-draft-title" />
                            {aiDraft.sections.map((s, i) => (
                                <div key={i}>
                                    <div className="text-xs font-bold text-[#0F766E] uppercase tracking-wider mb-1">
                                        {s.heading}
                                    </div>
                                    <Textarea rows={5} value={s.body}
                                        onChange={(e) => updateDraftSection(i, e.target.value)}
                                        data-testid={`guide-ai-draft-section-${i}`} />
                                </div>
                            ))}
                            <div>
                                <div className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">Disclaimer</div>
                                <Textarea rows={2} value={aiDraft.disclaimer}
                                    onChange={(e) => setAiDraft({ ...aiDraft, disclaimer: e.target.value })}
                                    data-testid="guide-ai-draft-disclaimer" />
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <Button onClick={saveDraftEdits} variant="outline"
                                    data-testid="guide-ai-save">Save edits</Button>
                                <Button onClick={aiPublish} disabled={publishBusy}
                                    data-testid="guide-ai-publish" className="bg-[#123B5D]">
                                    {publishBusy ? "Publishing…" : "Publish as PDF"}
                                </Button>
                                <span className="text-[11px] text-[#64748B]">
                                    Model: {aiDraft.model || "claude-sonnet-5-5"} · ~{aiDraft.estimated_words} words
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(data.items || []).map((it) => (
                    <div key={it.id} className="rounded-xl border border-[#E2E8F0] bg-white p-4 flex items-start gap-3"
                        data-testid={`guide-item-${it.id}`}>
                        <div className="w-10 h-10 rounded-lg bg-[#0F766E]/10 flex items-center justify-center flex-shrink-0">
                            <FileDown size={18} className="text-[#0F766E]" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-semibold text-[#123B5D] flex items-center gap-2">
                                {it.title}
                                {it.source === "ai_drafted" && (
                                    <Badge variant="outline" className="text-[10px] text-[#0F766E] border-[#0F766E]/30">
                                        ✨ AI-drafted
                                    </Badge>
                                )}
                            </div>
                            {it.category && <div className="text-[11px] text-[#0F766E] font-bold tracking-wider uppercase mt-0.5">{it.category}</div>}
                            {it.summary && <p className="text-xs text-slate-600 mt-2 leading-relaxed">{it.summary}</p>}
                            <div className="text-[11px] text-slate-500 mt-2">
                                {it.pdf_filename} · {Math.round((it.pdf_size || 0) / 1024)} KB · {it.download_count || 0} downloads
                            </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <Switch checked={!!it.is_visible} onCheckedChange={() => toggle(it)}
                                data-testid={`guide-toggle-${it.id}`} />
                            <a href={it.pdf_url} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-slate-600 underline">Preview ↗</a>
                            <Button variant="ghost" size="icon" onClick={() => remove(it.id)}
                                data-testid={`guide-delete-${it.id}`}>
                                <Trash2 size={15} className="text-rose-600" />
                            </Button>
                        </div>
                    </div>
                ))}
                {!(data.items || []).length && (
                    <div className="col-span-full text-sm text-[#64748B] rounded-2xl border border-dashed border-[#CBD5E1] bg-[#F8FAFC] p-6 text-center">
                        No guides uploaded yet.
                    </div>
                )}
            </div>
        </div>
    );
}


export default function DoctorContentTrust() {
    const [doctor, setDoctor] = useState(null);
    useEffect(() => { api.get("/me/doctor").then((r) => setDoctor(r.data)).catch(() => {}); }, []);

    return (
        <DoctorShell doctor={doctor}>
            <div className="max-w-5xl space-y-6" data-testid="b3-content-trust-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#123B5D]">Content &amp; Trust</h1>
                    <p className="text-sm text-[#64748B] mt-1">
                        Voice intro, video testimonials, before/after cases, award certificates, media mentions
                        and downloadable health guides. Each section auto-hides on your public profile when empty.
                    </p>
                </div>
                <Card className="border-[#E5E9F2] rounded-2xl shadow-none">
                    <CardContent className="pt-5">
                        <Tabs defaultValue="voice" className="w-full">
                            <TabsList className="flex flex-wrap h-auto gap-1 bg-[#F5F8FA] p-1">
                                <TabsTrigger value="voice" data-testid="tab-voice"><Mic size={14} className="mr-1.5" /> Voice intro</TabsTrigger>
                                <TabsTrigger value="videos" data-testid="tab-videos"><Video size={14} className="mr-1.5" /> Video testimonials</TabsTrigger>
                                <TabsTrigger value="cases" data-testid="tab-cases"><Images size={14} className="mr-1.5" /> Case gallery</TabsTrigger>
                                <TabsTrigger value="awards" data-testid="tab-awards"><Award size={14} className="mr-1.5" /> Awards</TabsTrigger>
                                <TabsTrigger value="media" data-testid="tab-media"><Newspaper size={14} className="mr-1.5" /> Media</TabsTrigger>
                                <TabsTrigger value="guides" data-testid="tab-guides"><FileDown size={14} className="mr-1.5" /> Health guides</TabsTrigger>
                            </TabsList>
                            <TabsContent value="voice" className="pt-6"><VoiceIntroTab /></TabsContent>
                            <TabsContent value="videos" className="pt-6"><VideoTestimonialsTab /></TabsContent>
                            <TabsContent value="cases" className="pt-6"><CaseGalleryTab /></TabsContent>
                            <TabsContent value="awards" className="pt-6"><AwardsTab /></TabsContent>
                            <TabsContent value="media" className="pt-6"><MediaMentionsTab /></TabsContent>
                            <TabsContent value="guides" className="pt-6"><HealthGuidesTab /></TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>
            </div>
        </DoctorShell>
    );
}
